import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Card, Row, Col, DatePicker, Button, Space, Statistic, Tag, Spin, message } from 'antd';
import { 
  DollarOutlined, 
  BarChartOutlined, 
  DownloadOutlined, 
  PrinterOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../services/api';
import ReportLayout from '../ReportLayout';
import '../report-common.css'; // CSS chung cho tất cả báo cáo

const { RangePicker } = DatePicker;

interface ProfitReportData {
  product_code: string;
  product_name: string;
  total_merchandise: number;
  total_discount: number;
  revenue: number;
  total_cost: number;
  gross_profit: number;
  quantity_sold: number;
}

interface ProfitSummary {
  total_transactions: number;
  total_merchandise: number;
  total_discount: number;
  total_revenue: number;
  total_cost: number;
  total_profit: number;
}

const ProfitReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch profit report data
  const { data: reportData, error, refetch } = useQuery(
    ['profit-report', dateRange],
    async () => {
      const response = await api.get('/api/reports/profit-detail', {
        params: {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD')
        }
      });
      return response.data;
    },
    {
      enabled: !!dateRange[0] && !!dateRange[1],
      staleTime: 5 * 60 * 1000,
      retry: 3,
      onError: (error) => {
        console.error('Profit report API error:', error);
        message.error('Có lỗi xảy ra khi tải dữ liệu báo cáo');
      }
    }
  );

  const handleDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    message.info('Tính năng xuất báo cáo đang được phát triển');
  };

  const handlePrint = () => {
    window.print();
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  // Calculate summary from data
  const calculateSummary = (data: ProfitReportData[]): ProfitSummary => {
    return data.reduce((summary, item) => ({
      total_transactions: summary.total_transactions + 1,
      total_merchandise: summary.total_merchandise + item.total_merchandise,
      total_discount: summary.total_discount + item.total_discount,
      total_revenue: summary.total_revenue + item.revenue,
      total_cost: summary.total_cost + item.total_cost,
      total_profit: summary.total_profit + item.gross_profit
    }), {
      total_transactions: 0,
      total_merchandise: 0,
      total_discount: 0,
      total_revenue: 0,
      total_cost: 0,
      total_profit: 0
    });
  };

  const reportItems = reportData?.data || [];
  const summary = calculateSummary(reportItems);

  const sidebar = (
    <div>
      {/* Date Range */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#2c3e50' }}>Chọn khoảng thời gian</h4>
        <RangePicker
          value={dateRange}
          onChange={handleDateChange}
          style={{ width: '100%' }}
          format="DD/MM/YYYY"
        />
      </div>
    </div>
  );

  return (
    <ReportLayout sidebar={sidebar}>
      <div className="reportContainer">
        {/* Report Header */}
        <div className="reportHeader">
          <h1 className="reportTitle">BÁO CÁO LỢI NHUẬN THEO SẢN PHẨM</h1>
          <div className="reportSubtitle">
            Từ {dateRange[0].format('DD/MM/YYYY')} đến {dateRange[1].format('DD/MM/YYYY')}
          </div>
        </div>

        {/* Report Table */}
        <div className="reportTable">
          <div className="tableWrapper">
            <table className="table">
                             <thead className="tableHeader">
                 <tr>
                   <th className="headerCell">STT</th>
                   <th className="headerCell">Mã sản phẩm</th>
                   <th className="headerCell">Tên sản phẩm</th>
                   <th className="headerCell">Tổng tiền hàng</th>
                   <th className="headerCell">Giảm giá</th>
                   <th className="headerCell">Doanh thu</th>
                   <th className="headerCell">Tổng giá vốn</th>
                   <th className="headerCell">Lợi nhuận gộp</th>
                 </tr>
               </thead>
              <tbody>
                                 {/* Summary Row */}
                 <tr className="summaryRow">
                   <td className="summaryCell"></td>
                   <td className="summaryCell">
                     <strong>SL giao dịch: {summary.total_transactions}</strong>
                   </td>
                   <td className="summaryCell"></td>
                   <td className="summaryCell" style={{ textAlign: 'right' }}>
                     <strong>{formatCurrency(summary.total_merchandise)}</strong>
                   </td>
                   <td className="summaryCell" style={{ textAlign: 'right' }}>
                     <strong>{formatCurrency(summary.total_discount)}</strong>
                   </td>
                   <td className="summaryCell" style={{ textAlign: 'right' }}>
                     <strong>{formatCurrency(summary.total_revenue)}</strong>
                   </td>
                   <td className="summaryCell" style={{ textAlign: 'right' }}>
                     <strong>{formatCurrency(summary.total_cost)}</strong>
                   </td>
                   <td className="summaryCell" style={{ textAlign: 'right' }}>
                     <strong>{formatCurrency(summary.total_profit)}</strong>
                   </td>
                 </tr>

                {/* Detail Rows */}
                                 {isLoading ? (
                   <tr>
                     <td colSpan={8} className="loadingCell">
                       <Spin size="large" />
                     </td>
                   </tr>
                                 ) : reportItems.length === 0 ? (
                   <tr>
                     <td colSpan={8} className="emptyCell">
                       Không có dữ liệu trong khoảng thời gian đã chọn
                     </td>
                   </tr>
                 ) : (
                   reportItems.map((item: ProfitReportData, index: number) => (
                     <tr key={item.product_code} className={index % 2 === 0 ? 'evenRow' : 'oddRow'}>
                       <td className="detailCell">{index + 1}</td>
                       <td className="detailCell">{item.product_code}</td>
                       <td className="detailCell">{item.product_name}</td>
                       <td className="detailCell" style={{ textAlign: 'right' }}>{formatCurrency(item.total_merchandise)}</td>
                       <td className="detailCell" style={{ textAlign: 'right' }}>
                         {item.total_discount > 0 ? `-${formatCurrency(item.total_discount)}` : formatCurrency(0)}
                       </td>
                       <td className="detailCell" style={{ textAlign: 'right' }}>{formatCurrency(item.revenue)}</td>
                       <td className="detailCell" style={{ textAlign: 'right' }}>{formatCurrency(item.total_cost)}</td>
                       <td className="detailCell" style={{ textAlign: 'right' }}>
                         <span style={{ color: item.gross_profit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                           {formatCurrency(item.gross_profit)}
                         </span>
                       </td>
                     </tr>
                   ))
                 )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ReportLayout>
  );
};

export default ProfitReport;
